import { useI18n } from '@/ui/i18n/I18nProvider';
import { Icon } from '@/ui/components/Icon';
import styles from './LoadingScreen.module.css';

// Bundled (not from the art manifest): the loading screen is up before the manifest arrives.
const ILLUSTRATION = Object.values(
  import.meta.glob<string>('../art/skin/ill_loading.webp', { eager: true, import: 'default' }),
)[0];

/**
 * S00, shown until the first scene is drawn; fades out instead of asking for "tap to start". Once the room
 * starts loading, a bar shows how far it got (M0-18).
 */
export function LoadingScreen({ visible, progress }: { visible: boolean; progress?: number }) {
  const { t } = useI18n();
  return (
    <div className={styles.screen} data-visible={visible} aria-hidden={!visible} data-testid="loading-screen">
      <div className={styles.card}>
        {ILLUSTRATION && <img className={styles.art} src={ILLUSTRATION} alt="" draggable={false} />}
        <h1 className={styles.title}>
          <Icon name="sparkle" size={22} className={styles.spark} />
          {t('app.title')}
        </h1>
        <p className={styles.tagline}>{t('app.tagline')}</p>
        <div className={styles.dots} role="status" aria-live="polite">
          <span />
          <span />
          <span />
          <span className="visually-hidden">{t('ui.loading.opening')}</span>
        </div>
        {progress !== undefined && (
          <div
            className={styles.bar}
            role="progressbar"
            aria-label={t('ui.loading.opening')}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            data-testid="loading-progress"
          >
            <span style={{ width: `${String(Math.round(progress * 100))}%` }} />
          </div>
        )}
      </div>
      <p className={styles.footer}>{t('ui.loading.made_by')}</p>
    </div>
  );
}
